import { useEffect, useState } from 'react';
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router';
import {
  Box,
  Button,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Link as ChakraLink,
  Text,
  HStack,
  Image,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuthContext } from '@/context/auth/useAuthContext';
import appMessages from '@/messages';
import authMessages from '@/messages/auth';
import authRoutes from '@/constants/routes/auth';
import AuthenticationPage from '@/components/templates/AuthenticationPage';
import iconsPng from '@/assets/img/png/icons';
import { TCustomAxiosError } from '@/services/api';
import { EMAIL_REGEX } from '@/constants/stringVars';
import CustomButton from '@/components/atoms/CustomButton';

type FormData = {
  email: string;
  password: string;
};

type SignInSearch = {
  email?: string;
  linkBack?: string;
};

const DEFAULT_SIGN_IN_ERROR_MESSAGE = 'Unable to sign in.';

const getSignInErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message || DEFAULT_SIGN_IN_ERROR_MESSAGE;
  }

  const apiErrorMessage =
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as TCustomAxiosError).errors)
      ? (error as TCustomAxiosError).errors[0]?.message
      : null;

  return apiErrorMessage || DEFAULT_SIGN_IN_ERROR_MESSAGE;
};

// NOTE: If we expect some custom validation rules, consider moving yup instance to separate file and import from there
const schema = yup
  .object({
    email: yup.string().required('Email is required').matches(EMAIL_REGEX, 'Please enter valid email'),
    password: yup.string().required('Password is required'),
  })
  .required();

const SignIn = () => {
  const router = useRouter();
  const search = Route.useSearch();
  const { signIn } = useAuthContext();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      email: search.email ?? '',
      password: '',
    },
    resolver: yupResolver(schema),
    mode: 'all',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [customErrorMessage, setCustomErrorMessage] = useState('');

  const { mutateAsync: handleSignIn, isPending: isHandleSignInPending } = useMutation({
    mutationFn: async (data: FormData) => {
      setCustomErrorMessage('');
      await signIn(data.email, data.password);
      router.invalidate();
    },
    onError: (error: unknown) => {
      console.log('error', error);
      const customDataError = getSignInErrorMessage(error);
      setCustomErrorMessage(customDataError.toUpperCase());
    },
  });

  useEffect(() => {
    if (search.email) {
      setValue('email', search.email);
    }
  }, [search.email, setValue]);

  return (
    <AuthenticationPage>
      <Text
        variant={'urbanistSemiBold'}
        textColor={'text.veryLightBlue'}
        color={'background.lightBlue'}
        opacity={0.7}
        position={'relative'}
        marginTop={'20px'}
        maxWidth={'400px'}
      >
        {appMessages.signInDescription}
      </Text>
      <Box minW={{ md: '295px' }} marginTop={'40px'} alignItems={'center'}>
        <form onSubmit={handleSubmit((data) => handleSignIn(data))}>
          <Stack spacing={4} p="1rem">
            <FormControl>
              <Input
                className="custom-input"
                {...register('email', {
                  onChange: () => {
                    if (customErrorMessage) setCustomErrorMessage('');
                  },
                })}
                placeholder={appMessages.email}
              />
              <Text variant={'errorYellow'}>{errors.email?.message ?? ''}</Text>
            </FormControl>
            <FormControl>
              <InputGroup>
                <Input
                  className="custom-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={appMessages.password}
                  {...register('password', {
                    onChange: () => {
                      if (customErrorMessage) setCustomErrorMessage('');
                    },
                  })}
                />
                <InputRightElement width="4rem" height={'100%'}>
                  <Button onClick={() => setShowPassword(!showPassword)} backgroundColor={'transparent'}>
                    <Image
                      src={showPassword ? iconsPng.eyeOpened : iconsPng.eyeClosed}
                      width={25}
                      height={25}
                      alt={showPassword ? 'eyeOpened' : 'eyeClosed'}
                    />
                  </Button>
                </InputRightElement>
              </InputGroup>

              <Text variant={'errorYellow'}>{errors.password?.message ?? ''}</Text>

              <Box position={'relative'} width={'300px'} mt={'10px'}>
                <Text
                  color={'extra.yellowStrong'}
                  fontWeight={'900'}
                  fontSize={'13px'}
                  position={'absolute'}
                  top={'0'}
                  width={'100%'}
                  textAlign={'center'}
                  paddingX={'16px'}
                  lineHeight={'18px'}
                >
                  {customErrorMessage}
                </Text>
              </Box>
            </FormControl>
            <CustomButton
              label={authMessages.signIn}
              backgroundColor="secondary.500"
              labelColor="white"
              width="295px"
              isLoading={isHandleSignInPending}
              buttonType="submit"
              disabled={!isValid}
              style={{ marginTop: '40px' }}
            />
          </Stack>
        </form>
        <HStack width={'100%'} justifyContent={'center'}>
          <ChakraLink
            as={Link}
            to={authRoutes.forgotPassword}
            color="text.veryLightBlue"
            fontSize={'16px'}
            fontWeight={'600'}
            lineHeight={'20px'}
            textAlign={'center'}
          >
            {authMessages.forgotPassword}
          </ChakraLink>
        </HStack>
        <HStack
          width={'100%'}
          justifyContent={'center'}
          marginTop={'34px'}
          spacing={'4px'}
          flexWrap={'wrap'}
          textAlign={'center'}
        >
          <Text
            color={'text.veryLightBlue'}
            fontSize={{ base: '11px', md: '14px' }}
            fontWeight={'700'}
            lineHeight={{ base: '14px', md: '18px' }}
          >
            Are you a Creator?
          </Text>
          <ChakraLink
            as={Link}
            to={'/creator/apply'}
            color={'white'}
            fontSize={{ base: '11px', md: '14px' }}
            fontWeight={'800'}
            lineHeight={{ base: '14px', md: '18px' }}
            display={'inline-flex'}
            alignItems={'center'}
            gap={'4px'}
          >
            Apply Here <ArrowForwardIcon boxSize={{ base: '11px', md: '13px' }} />
          </ChakraLink>
        </HStack>
      </Box>
    </AuthenticationPage>
  );
};

export const Route = createFileRoute('/sign-in/')({
  staticData: { title: 'Sign In' },
  component: SignIn,
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    email: typeof search.email === 'string' ? search.email : undefined,
    linkBack: typeof search.linkBack === 'string' ? search.linkBack : undefined,
  }),
  beforeLoad: async ({ context: { auth }, search }) => {
    if (auth?.isAuthenticated) {
      throw redirect({
        to: '/onboarding/user', // all re-routing and checks are done in _onboarding.tsx
        search: search.linkBack
          ? {
              linkBack: search.linkBack,
            }
          : {},
      });
    }
  },
});
